import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BookingDocket } from "../components/BookingDocket";
import { Icon } from "../components/Icon";
import { LoadingState } from "../components/LoadingState";
import { SiteHeader } from "../components/SiteHeader";
import { cancelBooking, defaultBookingRange, getAvailability, getManagedBooking, rescheduleBooking } from "../lib/api";
import { browserTimeZone, formatDay, formatFullDateTime, formatTime, toDateKey } from "../lib/date";
import { downloadBookingIcs } from "../lib/ics";
import type { AvailableSlot, ManagedBooking } from "../lib/types";

export function ManageBookingPage() {
  const { token = "" } = useParams();
  const [booking, setBooking] = useState<ManagedBooking | null>(null);
  const [slots, setSlots] = useState<AvailableSlot[]>([]);
  const [mode, setMode] = useState<"details" | "reschedule" | "cancel">("details");
  const [working, setWorking] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const range = useMemo(() => defaultBookingRange(), []);

  useEffect(() => {
    void getManagedBooking(token)
      .then(setBooking)
      .catch((caught: unknown) => setError(caught instanceof Error ? caught.message : "This booking could not be loaded."))
      .finally(() => setLoading(false));
  }, [token]);

  const loadSlots = async () => {
    if (!booking) return;
    setMode("reschedule");
    setWorking(true);
    setError(null);
    try {
      setSlots(await getAvailability(booking.event_slug, range.from, range.to));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Available times could not be loaded.");
    } finally {
      setWorking(false);
    }
  };

  const reschedule = async (slot: AvailableSlot) => {
    setWorking(true);
    setError(null);
    try {
      setBooking(await rescheduleBooking(token, slot.starts_at));
      setMode("details");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The meeting could not be rescheduled.");
    } finally {
      setWorking(false);
    }
  };

  const cancel = async () => {
    setWorking(true);
    setError(null);
    try {
      setBooking(await cancelBooking(token, "Cancelled by guest"));
      setMode("details");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The meeting could not be cancelled.");
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="manage-page">
      <SiteHeader quiet />
      <main className="manage-shell">
        {loading ? <LoadingState label="Loading booking" /> : null}
        {error ? <div className="error-banner" role="alert">{error}</div> : null}
        {!loading && !booking && !error ? <p>This private booking link is not valid.</p> : null}
        {booking ? (
          <>
            <section className="manage-copy">
              <p className="section-label">Booking · {booking.reference}</p>
              <h1>{booking.status === "cancelled" ? "Meeting cancelled." : "Manage your meeting."}</h1>
              <p>
                {booking.status === "cancelled"
                  ? "The time is available to others again. You can book a new call whenever you are ready."
                  : formatFullDateTime(booking.starts_at, browserTimeZone)}
              </p>

              {booking.status !== "cancelled" && mode === "details" ? (
                <div className="manage-actions">
                  <button className="button button--primary" type="button" onClick={() => void loadSlots()}>
                    <Icon name="calendar" /> Reschedule
                  </button>
                  <button className="button button--outline" type="button" onClick={() => setMode("cancel")}>
                    Cancel meeting
                  </button>
                  <button className="text-button" type="button" onClick={() => downloadBookingIcs(booking)}>
                    <Icon name="download" /> Download calendar file
                  </button>
                </div>
              ) : null}

              {mode === "cancel" ? (
                <div className="manage-confirm" role="alert">
                  <h2>Release this time?</h2>
                  <p>This cancels the meeting for you and RSM.</p>
                  <div>
                    <button className="button button--danger" type="button" onClick={() => void cancel()} disabled={working}>Yes, cancel meeting</button>
                    <button className="button button--outline" type="button" onClick={() => setMode("details")}>Keep meeting</button>
                  </div>
                </div>
              ) : null}

              {mode === "reschedule" ? (
                <div className="reschedule-panel">
                  <div className="reschedule-panel__heading">
                    <h2>Choose a new time</h2>
                    <button className="text-button" type="button" onClick={() => setMode("details")}>Close</button>
                  </div>
                  {working && !slots.length ? <LoadingState label="Finding available times" /> : null}
                  <div className="reschedule-list">
                    {slots.slice(0, 18).map((slot) => (
                      <button type="button" key={slot.starts_at} onClick={() => void reschedule(slot)} disabled={working}>
                        <span>{formatDay(toDateKey(new Date(slot.starts_at)), browserTimeZone)}</span>
                        <strong>{formatTime(slot.starts_at, browserTimeZone)}</strong>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              {booking.status === "cancelled" ? (
                <Link className="button button--primary" to={`/book/${booking.event_slug}`}>Book a new time <Icon name="arrow" /></Link>
              ) : null}
            </section>
            <BookingDocket booking={booking} viewerTimeZone={browserTimeZone} />
          </>
        ) : null}
      </main>
    </div>
  );
}
